import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatError, downloadReport } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleOf } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AiReport from "@/components/AiReport";
import ThesisProgress from "@/components/ThesisProgress";
import {
  ArrowLeft, Plus, Loader2, FileText, Flag, MessageSquare, CheckCircle2, Clock, Lock, Upload, Download, ShieldAlert, FileDown, UserCog, Sparkles,
} from "lucide-react";

const CH_STATUS = {
  analyzing: { c: "bg-blue-100 text-blue-800", l: "AI Analyzing" },
  pending_feedback: { c: "bg-amber-100 text-amber-800", l: "Awaiting Supervisor" },
  approved: { c: "bg-emerald-100 text-emerald-800", l: "Approved" },
  revise: { c: "bg-orange-100 text-orange-800", l: "Revision Requested" },
};

export default function SupervisionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = roleOf(user);
  const isStudent = role === "student";
  const canFeedback = ["supervisor", "tenant_admin", "super_admin"].includes(role);
  const canReassign = ["director", "tenant_admin", "super_admin"].includes(role);
  const [data, setData] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [newSup, setNewSup] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [handoverNote, setHandoverNote] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [chOpen, setChOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [chForm, setChForm] = useState({ title: "", content: "", response_matrix: "" });
  const [chFile, setChFile] = useState(null);
  const [msOpen, setMsOpen] = useState(false);
  const [msForm, setMsForm] = useState({ title: "", due_date: "" });
  const [note, setNote] = useState("");
  const [fbChapter, setFbChapter] = useState(null);
  const [fb, setFb] = useState({ feedback: "", status: "approved", next_milestone: "" });

  const load = () => api.get(`/supervisions/${id}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (canReassign) api.get("/supervisors").then((r) => setSupervisors(r.data)).catch(() => {}); }, [canReassign]);

  const reassign = async () => {
    try {
      await api.patch(`/supervisions/${id}/reassign`, { supervisor_id: newSup, reason: reassignReason, handover_note: handoverNote });
      toast.success("Supervisor reassigned — both supervisors and the student were notified");
      setReassignOpen(false); setNewSup(""); setReassignReason(""); setHandoverNote(""); setSuggestions([]); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const suggestSup = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.get(`/supervisions/${id}/suggest-supervisor`);
      setSuggestions(data.suggestions || []);
      if (!(data.suggestions || []).length) toast.info("No supervisor suggestions available.");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSuggesting(false); }
  };
  useEffect(() => {
    if (!data?.chapters?.some((c) => c.status === "analyzing")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin brand-text" /></div>;

  const prog = data.progression;
  const nextStage = prog?.next_submittable;
  const isRevision = nextStage && prog?.stages?.find((s) => s.name === nextStage)?.status === "revise";

  const openSubmit = () => { setChForm({ title: nextStage, content: "", response_matrix: "" }); setChFile(null); setChOpen(true); };

  const submitChapter = async () => {
    if (!chFile && !chForm.content.trim()) { toast.error("Upload a file or paste your chapter text."); return; }
    setSubmitting(true);
    try {
      if (chFile) {
        const fd = new FormData();
        fd.append("supervision_id", id); fd.append("title", chForm.title);
        fd.append("response_matrix", chForm.response_matrix || ""); fd.append("file", chFile);
        await api.post("/chapters/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/chapters", { supervision_id: id, ...chForm });
      }
      toast.success("Chapter submitted — AI diagnostic in progress");
      setChOpen(false); setChForm({ title: "", content: "", response_matrix: "" }); setChFile(null);
      setTimeout(load, 1500);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const releaseFeedback = async () => {
    try {
      await api.patch(`/chapters/${fbChapter.id}/feedback`, fb);
      toast.success(fb.status === "approved" ? "Chapter approved — next chapter unlocked" : "Revision requested");
      setFbChapter(null); setFb({ feedback: "", status: "approved", next_milestone: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const addMilestone = async () => {
    try {
      await api.post("/milestones", { supervision_id: id, ...msForm, due_date: new Date(msForm.due_date).toISOString() });
      toast.success("Milestone added"); setMsOpen(false); setMsForm({ title: "", due_date: "" }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!note) return;
    try { await api.post("/notes", { supervision_id: id, body: note }); setNote(""); toast.success("Note added"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <button data-testid="back-btn" onClick={() => navigate("/supervisions")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{data.student_name}</h1>
          <p className="text-muted-foreground">Supervisor: {data.supervisor_name}{data.programme ? ` · ${data.programme}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="brand-bg text-white text-sm">{data.stage}</Badge>
          {canReassign && (
            <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
              <DialogTrigger asChild>
                <Button data-testid="reassign-btn" variant="outline" size="sm"><UserCog className="h-4 w-4 mr-1" />Reassign</Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle>Reassign supervisor</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">Move <strong>{data.student_name}</strong> from <strong>{data.supervisor_name}</strong> to another supervisor.</p>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">New supervisor</Label>
                      <Button data-testid="reassign-suggest" size="sm" variant="outline" onClick={suggestSup} disabled={suggesting}>
                        {suggesting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}Suggest best-fit
                      </Button>
                    </div>
                    {suggestions.length > 0 && (
                      <div className="space-y-1.5 my-2" data-testid="reassign-suggestions">
                        {suggestions.map((s) => (
                          <button key={s.supervisor_id} type="button" data-testid={`reassign-suggestion-${s.supervisor_id}`}
                            onClick={() => setNewSup(s.supervisor_id)}
                            className={`w-full text-left rounded-md border p-2 text-sm transition-colors ${newSup === s.supervisor_id ? "brand-border bg-secondary" : "border-border hover:bg-secondary/50"}`}>
                            <span className="font-medium">{s.name}</span>
                            <Badge variant="outline" className="ml-2 text-[10px] capitalize">{s.match} fit</Badge>
                            <span className="block text-xs text-muted-foreground mt-0.5">{s.reason}</span>
                          </button>
                        ))}
                        <p className="text-[11px] text-muted-foreground">AI suggests; you decide.</p>
                      </div>
                    )}
                    <Select value={newSup} onValueChange={setNewSup}>
                      <SelectTrigger data-testid="reassign-select"><SelectValue placeholder="Choose a supervisor" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {supervisors.filter((s) => s.id !== data.supervisor_id).map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}{s.expertise ? ` · ${s.expertise}` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Reason (optional)</Label><Input data-testid="reassign-reason" value={reassignReason} onChange={(e) => setReassignReason(e.target.value)} placeholder="e.g. workload rebalancing" /></div>
                  <div><Label className="text-xs">Handover note (optional)</Label><Textarea data-testid="reassign-handover" rows={3} value={handoverNote} onChange={(e) => setHandoverNote(e.target.value)} placeholder="Context for the new supervisor — student's progress, outstanding issues, expectations…" /></div>
                </div>
                <DialogFooter>
                  <Button data-testid="reassign-submit" onClick={reassign} disabled={!newSup} className="brand-bg text-white">Reassign & notify</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Tabs defaultValue="chapters">
        <TabsList>
          <TabsTrigger value="chapters" data-testid="tab-chapters">Chapters</TabsTrigger>
          <TabsTrigger value="milestones" data-testid="tab-milestones">Milestones</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Meeting Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="chapters" className="mt-4">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Progression sidebar */}
            <Card className="p-5 h-fit">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-4 w-4 brand-text" />
                <h3 className="font-heading font-semibold text-sm">Thesis progression</h3>
              </div>
              <ThesisProgress progression={prog} />
              <p className="text-[11px] text-muted-foreground mt-3 border-t border-border pt-3">
                Per ARU guidelines, chapters are reviewed one at a time. The next chapter unlocks only after the previous is approved.
              </p>
            </Card>

            {/* Chapters column */}
            <div className="lg:col-span-2 space-y-3">
              {isStudent && (
                nextStage ? (
                  <Dialog open={chOpen} onOpenChange={setChOpen}>
                    <Button data-testid="new-chapter-btn" onClick={openSubmit} className="brand-bg text-white">
                      <Upload className="h-4 w-4 mr-1" />{isRevision ? "Resubmit" : "Submit"} {nextStage}
                    </Button>
                    <DialogContent className="bg-white max-w-2xl">
                      <DialogHeader><DialogTitle>{isRevision ? "Resubmit" : "Submit"} — {chForm.title}</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label>Chapter</Label>
                          <Input data-testid="ch-title" value={chForm.title} readOnly className="bg-muted/50" />
                        </div>
                        <div>
                    <Label>Upload document (PDF, Word .docx, or text)</Label>
                    <input data-testid="ch-file" type="file" accept=".pdf,.docx,.txt,.md" onChange={(e) => setChFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[rgb(var(--brand))] file:text-white file:px-3 file:py-1.5 file:text-sm cursor-pointer" />
                    {chFile && <p className="text-xs text-muted-foreground mt-1">Selected: {chFile.name}</p>}
                  </div>
                  <div className="text-center text-xs text-muted-foreground">— or —</div>
                  <div><Label>Paste text {chFile ? "(optional)" : ""}</Label><Textarea data-testid="ch-content" rows={7} value={chForm.content} onChange={(e) => setChForm({ ...chForm, content: e.target.value })} placeholder="Paste your chapter text (following the ARU structure for this chapter)…" /></div>
                        <div><Label>Response-to-comments matrix (for resubmissions)</Label><Textarea data-testid="ch-matrix" rows={3} value={chForm.response_matrix} onChange={(e) => setChForm({ ...chForm, response_matrix: e.target.value })} placeholder="How you addressed each prior supervisor comment…" /></div>
                      </div>
                      <DialogFooter><Button data-testid="ch-submit" onClick={submitChapter} disabled={submitting || (!chFile && !chForm.content)} className="brand-bg text-white">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for AI Diagnostic"}</Button></DialogFooter>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <div className="rounded-md border border-border bg-secondary/40 p-4 flex items-start gap-2" data-testid="submit-blocked">
                    <Lock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <p className="text-sm text-muted-foreground">{prog?.blocked_reason || "No chapter to submit right now."}</p>
                  </div>
                )
              )}

              {data.chapters.map((c) => {
                const s = CH_STATUS[c.status] || { c: "bg-slate-100", l: c.status };
                return (
                  <Card key={c.id} className="p-5" data-testid={`chapter-${c.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2"><FileText className="h-4 w-4 brand-text" /><h3 className="font-heading font-semibold">{c.title}</h3><Badge variant="outline">v{c.version}</Badge></div>
                      <div className="flex items-center gap-2">
                        <Badge className={s.c}>{s.l}</Badge>
                        {!isStudent && c.ai_report?.integrity_signals?.length > 0 && (
                          <Badge className="bg-red-100 text-red-700 gap-1" data-testid={`chapter-integrity-${c.id}`}><ShieldAlert className="h-3 w-3" />{c.ai_report.integrity_signals.length}</Badge>
                        )}
                      </div>
                    </div>
                    {c.file?.file_id && (
                      <a data-testid={`chapter-download-${c.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.file.file_id}/download`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs brand-text hover:underline">
                        <Download className="h-3 w-3" /> {c.file.original_filename}
                      </a>
                    )}
                    {isStudent && !c.supervisor_feedback ? (
                      <div className="mt-4 rounded-md border border-border bg-secondary/40 p-4 text-sm text-muted-foreground" data-testid="chapter-under-review">
                        {c.status === "analyzing" ? "AI diagnostic is being prepared…" : "Submitted for review. Your supervisor will release feedback shortly — the AI assistant's diagnostic is prepared for their review."}
                      </div>
                    ) : (
                      <div className="mt-4"><AiReport report={c.ai_report} error={c.ai_error} /></div>
                    )}
                    {c.supervisor_feedback && (
                      <div className="mt-4 rounded-md border border-border bg-secondary/40 p-4">
                        <p className="text-sm font-medium flex items-center gap-1">
                          {c.status === "approved" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Flag className="h-4 w-4 text-orange-600" />}
                          Supervisor decision: {c.status === "approved" ? "Approved" : "Revision requested"} ({c.feedback_by})
                        </p>
                        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{c.supervisor_feedback}</p>
                        {c.next_milestone && <p className="text-xs mt-2">Next: {c.next_milestone}</p>}
                      </div>
                    )}
                    {canFeedback && !c.supervisor_feedback && c.status !== "analyzing" && (
                      <Button size="sm" className="mt-4 brand-bg text-white" data-testid={`give-feedback-${c.id}`} onClick={() => { setFb({ feedback: "", status: "approved", next_milestone: "", email_report: false }); setFbChapter(c); }}><MessageSquare className="h-4 w-4 mr-1" />Review & Decide</Button>
                    )}
                    {canFeedback && c.ai_report && (
                      <button data-testid={`chapter-export-pdf-${c.id}`} onClick={async () => {
                        try { await downloadReport(`/chapters/${c.id}/report.pdf`, `integrity-report-${c.title}-v${c.version}.pdf`); }
                        catch (e) { toast.error("Could not generate the report PDF. Please try again."); }
                      }} className="mt-4 ml-2 inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-secondary transition-colors">
                        <FileDown className="h-4 w-4 brand-text" /> Export report (PDF)
                      </button>
                    )}
                  </Card>
                );
              })}
              {data.chapters.length === 0 && <p className="text-muted-foreground text-sm">No chapters submitted yet.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="milestones" className="space-y-3 mt-4">
          {canFeedback && (
            <Dialog open={msOpen} onOpenChange={setMsOpen}>
              <DialogTrigger asChild><Button data-testid="new-milestone-btn" className="brand-bg text-white"><Plus className="h-4 w-4 mr-1" />Add Milestone</Button></DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle>Add Milestone</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Title</Label><Input data-testid="ms-title" value={msForm.title} onChange={(e) => setMsForm({ ...msForm, title: e.target.value })} /></div>
                  <div><Label>Due date</Label><Input data-testid="ms-date" type="date" value={msForm.due_date} onChange={(e) => setMsForm({ ...msForm, due_date: e.target.value })} /></div>
                </div>
                <DialogFooter><Button data-testid="ms-add" onClick={addMilestone} className="brand-bg text-white">Add</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {data.milestones.map((m) => (
            <Card key={m.id} className="p-4 flex items-center justify-between" data-testid={`milestone-${m.id}`}>
              <div className="flex items-center gap-3">
                {m.status === "completed" ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Clock className="h-5 w-5 text-amber-600" />}
                <div><p className="font-medium text-sm">{m.title}</p><p className="text-xs text-muted-foreground">Due {new Date(m.due_date).toLocaleDateString()}</p></div>
              </div>
              <Badge className={m.status === "completed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{m.status}</Badge>
            </Card>
          ))}
          {data.milestones.length === 0 && <p className="text-muted-foreground text-sm">No milestones set.</p>}
        </TabsContent>

        <TabsContent value="notes" className="space-y-3 mt-4">
          {canFeedback && (
            <div className="flex gap-2">
              <Textarea data-testid="note-input" placeholder="Add a meeting note…" value={note} onChange={(e) => setNote(e.target.value)} />
              <Button data-testid="note-add" onClick={addNote} className="brand-bg text-white self-end">Add</Button>
            </div>
          )}
          {data.notes.map((n) => (
            <Card key={n.id} className="p-4" data-testid={`note-${n.id}`}>
              <p className="text-sm">{n.body}</p>
              <p className="text-xs text-muted-foreground mt-1">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
            </Card>
          ))}
          {data.notes.length === 0 && <p className="text-muted-foreground text-sm">No meeting notes yet.</p>}
        </TabsContent>
      </Tabs>

      {/* feedback dialog */}
      <Dialog open={!!fbChapter} onOpenChange={(v) => !v && setFbChapter(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>Review Decision — {fbChapter?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">AI findings are advisory; your judgment is final. Approving advances the student to the next chapter.</p>
            <div className="flex gap-2">
              {[["approved", "Approve & Advance"], ["revise", "Request Revision"]].map(([v, l]) => (
                <button key={v} data-testid={`fb-status-${v}`} onClick={() => setFb({ ...fb, status: v })}
                  className={`px-3 py-1.5 rounded-md text-sm border ${fb.status === v ? "brand-bg text-white brand-border" : "border-border"}`}>{l}</button>
              ))}
            </div>
            <Textarea data-testid="fb-text" rows={5} placeholder="Your feedback to the student…" value={fb.feedback} onChange={(e) => setFb({ ...fb, feedback: e.target.value })} />
            <Input data-testid="fb-next" placeholder="Next milestone (optional)" value={fb.next_milestone} onChange={(e) => setFb({ ...fb, next_milestone: e.target.value })} />
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input data-testid="fb-email-report" type="checkbox" checked={!!fb.email_report} onChange={(e) => setFb({ ...fb, email_report: e.target.checked })} className="h-4 w-4 accent-[rgb(var(--brand))]" />
              Email the integrity &amp; AI report (PDF) to the student
            </label>
          </div>
          <DialogFooter><Button data-testid="fb-release" onClick={releaseFeedback} disabled={!fb.feedback} className="brand-bg text-white"><Flag className="h-4 w-4 mr-1" />Submit Decision</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
